/**
 * Data Classification API Client
 * PROJ-042 - Data Classification & Canvas Enhancement
 */

import { fetchWithAuth } from './http-client';
import { parseStandardApiResponse } from './standard-api';
import type { DataClassification } from '@/types/data-classification';

const API_BASE = (import.meta.env.VITE_API_URL || '/api/v1').replace(/\/$/, '');
const API_ENV_VAR = 'VITE_API_URL';

// Backend schema (camelCase - matches Drizzle ORM output)
interface DataClassificationBackend {
  code: string;
  name: string;
  description?: string;
  level: 'L1' | 'L2' | 'L3' | 'L4' | 'L5';
  colorCode: string;
  requiresEncryption: boolean;
  requiresConsent: boolean;
  complianceStandards: string[];
  retentionGuidelines?: string;
  isActive: boolean;
}

// Transform backend classification to frontend format
// Backend already returns camelCase (Drizzle ORM), so this is a pass-through
function transformClassification(backend: DataClassificationBackend): DataClassification {
  return {
    code: backend.code,
    name: backend.name,
    description: backend.description,
    level: backend.level,
    colorCode: backend.colorCode,
    requiresEncryption: backend.requiresEncryption,
    requiresConsent: backend.requiresConsent,
    complianceStandards: backend.complianceStandards || [],
    retentionGuidelines: backend.retentionGuidelines,
    isActive: backend.isActive
  };
}

export async function getDataClassifications(): Promise<DataClassification[]> {
  const response = await fetchWithAuth(`${API_BASE}/data-classifications`, {
    context: 'Listado de clasificaciones de datos',
    envVar: API_ENV_VAR,
    baseUrl: API_BASE,
  });

  const { data } = await parseStandardApiResponse<DataClassificationBackend[]>(
    response,
    'Listado de clasificaciones de datos'
  );

  return data.map(transformClassification);
}

export async function getDataClassification(code: string): Promise<DataClassification> {
  const response = await fetchWithAuth(`${API_BASE}/data-classifications/${code}`, {
    context: 'Detalle de clasificación de datos',
    envVar: API_ENV_VAR,
    baseUrl: API_BASE,
  });

  const { data } = await parseStandardApiResponse<DataClassificationBackend>(
    response,
    'Detalle de clasificación de datos'
  );

  return transformClassification(data);
}
